import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import Big from 'big.js'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import CustomButton from '@/components/CustomButton'
import FixedBottom from '@/components/FixedBottom'
import Divider from '@/components/Divider'
import CardView from '../components/card-view'
import { CardFormFactor, parseCardMeta, parsePaymentSupports } from '../utils'

// 卡片类型枚举
export const enum CardType {
    Electronic = 'electronic',
    Physical = 'physical'
}

export default function CardDetail() {
    const { t, i18n } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()
    const [searchParams] = useSearchParams()

    // 从 URL 获取参数
    const cardType = (searchParams.get('type') as CardType) || CardType.Electronic
    const productId = searchParams.get('productId')

    // 从 location.state 获取产品数据
    const product = (location.state as { product?: ModelsCardProduct })?.product

    // 解析 meta 获取产品名称和描述
    // const { name: productName } = useMemo(() => {
    //     const locale = i18n.language || 'zh-CN'
    //     return parseCardMeta(product?.meta, locale)
    // }, [product?.meta, i18n.language])

    // 获取卡片背景图
    const backgroundImage = useMemo(() => {
        return (product as any)?.image || '/images/card-v2/electronic-card/zise-card-1.png'
    }, [product])

    // 获取支付支持
    const paymentSupports = useMemo(() => {
        return parsePaymentSupports(product?.features)
    }, [product?.features])

    // 是否为虚拟卡
    const isVirtual = product?.form_factor === CardFormFactor.Virtual

    /** 按卡产品 BIN 展示「添加持卡人」KYC 要求（4938 / 5258） */
    const cardholderNoticeKeys = useMemo(() => {
        const bin = product?.bin
        console.log(bin)
        if (bin === '4938') {
            return [
                '持卡人注意事项4938_1',
                '持卡人注意事项4938_2',
                '持卡人注意事项4938_3',
                '持卡人注意事项4938_4',
                '持卡人注意事项4938_5',
                '持卡人注意事项4938_6',
            ] as const
        }
        return [
            '持卡人注意事项5258_1',
            '持卡人注意事项5258_2',
            '持卡人注意事项5258_3',
            '持卡人注意事项5258_4',
            '持卡人注意事项5258_5',
            '持卡人注意事项5258_6',
        ] as const
    }, [product?.bin])

    // 处理申请卡片
    const handleApply = useCallback(() => {
        // 跳转到确认支付页面，传递产品数据
        navigate(`/card-v2/confirm-payment?type=${cardType}&productId=${productId}`, {
            state: { product }
        })
    }, [navigate, cardType, productId, product])

    return (
        <PageLayout>
            <Header
                leftChildren={<Back />}
                centerChildren={<span className="text-base font-medium">{t('申请卡片')}</span>}
            />

            <div className="overflow-y-auto px-5">
                {/* 卡片预览 */}
                <CardView
                    cardStatus="pending"
                    backgroundImage={backgroundImage}
                    cardBin={product?.bin}
                />

                {/* 卡优势 */}
                {/* <div className="bg-[#F7F8FA] rounded-xl p-4 my-6">
                    <div className="text-base font-semibold text-[#1D2129] mb-2">{t('卡优势')}</div>
                    <div className="text-sm text-[#86909C]">
                        {t('KYC友好')} | {t('低费率')} | {t('秒审核')}
                    </div>
                </div> */}

                {/* 产品特色 */}
                <div className='mt-6'>
                    <div className="flex justify-between items-center py-3 border-b border-[#F2F3F5]">
                        <span className="text-sm text-[#86909C]">{t('卡片币种')}</span>
                        <span className="text-sm text-[#1D2129] flex items-center">
                            <img src="/images/home/usd.png" alt="USD" className="w-5 h-5 mr-1" />
                            {product?.currency?.toLocaleUpperCase() || 'USD'}
                        </span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-[#F2F3F5]">
                        <span className="text-sm text-[#86909C]">{t('卡片类型')}</span>
                        <span className="text-sm text-[#1D2129]">
                            {isVirtual ? t('电子卡') : t('实体卡')}
                        </span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-[#F2F3F5]">
                        <span className="text-sm text-[#86909C]">{t('开卡费用')}</span>
                        <span className="text-sm text-[#1D2129]">
                            {product?.open_fee !== undefined ? `${product.open_fee} USD` : '-'}
                        </span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-[#F2F3F5]">
                        <span className="text-sm text-[#86909C]">{t('首充')}</span>
                        <span className="text-sm text-[#1D2129]">
                            {product?.min_open_deposit !== undefined ? `${product.min_open_deposit} USD` : '-'}
                        </span>
                    </div>
                </div>

                {/* 分隔线 */}
                <Divider type="dashed" className="my-6" />

                {/* 产品特色表格 */}
                <div>
                    <div className="text-base font-semibold text-[#1D2129] mb-4">{t('产品特色')}</div>

                    <div>
                        <div className="flex justify-between items-start py-2.5">
                            <span className="text-sm text-[#86909C] shrink-0">{t('月管理费')}</span>
                            <span className="text-sm text-[#1D2129] text-right flex-1 ml-4">
                                {product?.monthly_fee !== undefined ? `${product.monthly_fee} ${product?.currency?.toLocaleUpperCase() || 'USD'}` : '-'}
                            </span>
                        </div>
                        <div className="flex justify-between items-start py-2.5">
                            <span className="text-sm text-[#86909C] shrink-0">{t('授权费')}</span>
                            <span className="text-sm text-[#1D2129] text-right flex-1 ml-4">
                                {product?.auth_fee !== undefined ? `${product.auth_fee} ${product?.currency?.toLocaleUpperCase() || 'USD'}` : '-'}
                            </span>
                        </div>
                        <div className="flex justify-between items-start py-2.5">
                            <span className="text-sm text-[#86909C] shrink-0">{t('交易手续费')}</span>
                            <span className="text-sm text-[#1D2129] text-right flex-1 ml-4">
                                {product?.bin === '4938' ? '1% - 2%' : new Big(product?.service_fee_rate || 0).times(100).toFixed(2) + '%'}
                            </span>
                        </div>
                        <div className="flex justify-between items-start py-2.5">
                            <span className="text-sm text-[#86909C] shrink-0">{t('跨境手续费')}</span>
                            <span className="text-sm text-[#1D2129] text-right flex-1 ml-4">
                                {new Big(product?.exchange_diff_rate || 0).times(100).toFixed(2)}%
                            </span>
                        </div>
                        <div className="flex justify-between items-start py-2.5">
                            <span className="text-sm text-[#86909C] shrink-0">{t('撤销手续费')}</span>
                            <span className="text-sm text-[#1D2129] text-right flex-1 ml-4">
                                {product?.auth_failure_fee !== undefined ? `${product.auth_failure_fee} ${product?.currency?.toLocaleUpperCase() || 'USD'}` : '-'}
                            </span>
                        </div>
                        <div className="flex justify-between items-start py-2.5">
                            <span className="text-sm text-[#86909C] shrink-0">{t('退款手续费')}</span>
                            <span className="text-sm text-[#1D2129] text-right flex-1 ml-4">
                                {product?.auth_failure_fee !== undefined ? `${product.auth_failure_fee} ${product?.currency?.toLocaleUpperCase() || 'USD'}` : '-'}
                            </span>
                        </div>
                        {/* <div className="flex justify-between items-start py-2.5">
                            <span className="text-sm text-[#86909C] shrink-0">{t('充值手续费')}</span>
                            <span className="text-sm text-[#1D2129] text-right flex-1 ml-4">
                                {product?.deposit_fee !== undefined ? `${product.deposit_fee} ${product?.currency?.toLocaleUpperCase() || 'USD'}` : '-'}
                            </span>
                        </div> */}
                        <div className="flex justify-between items-start py-2.5">
                            <span className="text-sm text-[#86909C] shrink-0">{t('授权失败手续费')}</span>
                            <span className="text-sm text-[#1D2129] text-right flex-1 ml-4">
                                {product?.auth_failure_fee !== undefined ? `${product.auth_failure_fee} ${product?.currency?.toLocaleUpperCase() || 'USD'}` : '-'}
                            </span>
                        </div>
                        <div className="flex justify-between items-start py-2.5">
                            <span className="text-sm text-[#86909C] shrink-0">{t('单笔最高限额消费')}</span>
                            <span className="text-sm text-[#1D2129] text-right flex-1 ml-4">
                                {(() => {
                                    try {
                                        const limits = product?.default_limits ? (typeof product.default_limits === 'string' ? JSON.parse(product.default_limits) : product.default_limits) : null
                                        if (limits?.max_per_tran === -1) return t('不限')
                                        if (limits?.max_per_tran !== undefined) return `${limits.max_per_tran} ${product?.currency?.toLocaleUpperCase() || 'USD'}`
                                        return '-'
                                    } catch { return '-' }
                                })()}
                            </span>
                        </div>
                        <div className="flex justify-between items-start py-2.5">
                            <span className="text-sm text-[#86909C] shrink-0">{t('下卡时间')}</span>
                            <span className="text-sm text-[#1D2129] text-right flex-1 ml-4">{t('10分钟内下卡')}</span>
                        </div>
                    </div>
                </div>

                {/* 分隔线 */}
                <Divider type="dashed" className="my-6" />

                {/* 第三方支付支持 */}
                <div>
                    <div className="text-base font-semibold text-[#1D2129] mb-4">{t('第三方支付支持')}</div>

                    <div className="flex flex-col gap-3">
                        {paymentSupports.map((payment, index) => (
                            <div key={index} className="flex justify-between items-center py-2">
                                <div className="flex items-center gap-3">
                                    <div className="w-8.5 h-6 border border-[#EAECF0] border-solid rounded-1 flex items-center justify-center">
                                        <img src={payment.icon} alt={payment.name} />
                                    </div>
                                    <span className="text-sm text-[#1D2129]">{payment.name}</span>
                                </div>
                                {payment.supported && (
                                    <div className="w-5 h-5">
                                        <img src="/images/card-v2/success-status.svg" alt="" />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* 分隔线 */}
                <Divider type="dashed" className="my-6" />
                <div>
                    <div className="text-base font-semibold text-[#1D2129] mb-4">{t('添加持卡人注意事项')}</div>
                    <div className="flex flex-col gap-4 mt-4">
                        {cardholderNoticeKeys.map((key) => (
                            <div key={key} className="text-sm text-[#667085]">
                                {t(key)}
                            </div>
                        ))}
                    </div>
                </div>

                {/* 分隔线 */}
                <Divider type="dashed" className="my-6" />



                {/* 用卡须知 - 固定内容 */}
                <div className="pb-24">
                    <div className="text-base font-semibold text-[#1D2129] mb-4">{t('用卡须知')}</div>

                    <div className="flex flex-col gap-4">
                        <div className="text-xs text-[#86909C] leading-relaxed whitespace-pre-wrap">
                            <div className="text-sm text-[#1D2129] mb-1">1.{t('卡片激活')}</div>
                            <div className="text-sm text-[#86909C] leading-relaxed whitespace-pre-wrap">
                                {t('开卡后需要首充激活，首充最低10USD，建议充值30USD或以上，否则容易激活失败。')}
                            </div>
                        </div>
                        <div className="text-xs text-[#86909C] leading-relaxed whitespace-pre-wrap">
                            <div className="text-sm text-[#1D2129] mb-1">2.{t('恶意行为冻结')}</div>
                            <div className="text-sm text-[#86909C] leading-relaxed whitespace-pre-wrap">
                                {t('批量退款、恶意取消、恶意拒付等行为会导致卡片自动冻结，罚金10 美元/笔。')}
                            </div>
                        </div>
                        <div className="text-xs text-[#86909C] leading-relaxed whitespace-pre-wrap">
                            <div className="text-sm text-[#1D2129] mb-1">3.{t('交易失败锁卡')}</div>
                            <div className="text-sm text-[#86909C] leading-relaxed whitespace-pre-wrap">
                                {t('单张卡片若失败交易超过10笔且失败率超过20%，将被锁定，并收取每笔 0.5 美元 的罚金;若被认定为恶意行为，卡片将直接注销。')}
                            </div>
                        </div>
                        <div className="text-xs text-[#86909C] leading-relaxed whitespace-pre-wrap">
                            <div className="text-sm text-[#1D2129] mb-1">4.{t('异常授权冻结')}</div>
                            <div className="text-sm text-[#86909C] leading-relaxed whitespace-pre-wrap">
                                {t('多次授权未结算或金额为0，卡片将自动冻结。')}
                            </div>
                        </div>
                        <div className="text-xs text-[#86909C] leading-relaxed whitespace-pre-wrap">
                            <div className="text-sm text-[#1D2129] mb-1">5.{t('解冻流程')}</div>
                            <div className="text-sm text-[#86909C] leading-relaxed whitespace-pre-wrap">
                                {t('联系客服并提交交易申请表进行解冻。')}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* 固定在底部的申请按钮 */}
            <FixedBottom>
                <CustomButton onClick={handleApply}>
                    {t('申请卡片')}
                </CustomButton>
            </FixedBottom>
        </PageLayout>
    )
}
